import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import CreditsBadge from '../components/CreditsBadge';
import { useRefreshable } from '../context/RefreshContext';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import UserMenu from '../components/UserMenu';
import NavBrand from '../components/NavBrand';
import PageSpinner from '../components/PageSpinner';
import HeaderSearch from '../components/HeaderSearch';
import NotificationBell from '../components/NotificationBell';
import MessagesButton from '../components/MessagesButton';
import { fetchDesigners } from '../firebase/designerService';
import { fetchShops } from '../firebase/shopService';
import { fetchLikedUids } from '../firebase/likeService';
import './SearchPage.css';
import './MyDesigners.css';
import './Dashboard.css';
import BackBar from '../components/BackBar';
import PersonIcon from '../components/PersonIcon';

function DesignerCard({ d, t }) {
  return (
    <Link to={`/designer/${d.uid || d.id}`} className="sp-card">
      <div className="sp-avatar">
        {d.avatarUrl ? <img src={d.avatarUrl} alt="" /> : <PersonIcon />}
      </div>
      <div className="sp-card-body">
        <span className="sp-name">{d.fullName}</span>
        <span className="sp-meta">
          {[
            d.location,
            d.experienceYears != null && d.experienceYears !== ''
              ? t('ds_years_short').replace('{n}', d.experienceYears) : null,
          ].filter(Boolean).join(' · ')}
        </span>
        <span className="md-likes">♥ {d.likeCount || 0}</span>
      </div>
    </Link>
  );
}

function ShopCard({ s, t }) {
  return (
    <Link to={`/shop/${s.uid || s.id}`} className="sp-card">
      <div className="sp-avatar">
        {s.avatarUrl ? <img src={s.avatarUrl} alt="" /> : <PersonIcon />}
      </div>
      <div className="sp-card-body">
        <span className="sp-name">{s.name}</span>
        <span className="sp-meta">
          {[
            s.location,
            s.delivery ? t('shop_delivery') : null,
          ].filter(Boolean).join(' · ')}
        </span>
        <span className="md-likes">♥ {s.likeCount || 0}</span>
      </div>
    </Link>
  );
}

// ── MY DESIGNERS ─────────────────────────────────────────────────────────────
// The designers this user has liked, and — in a section of its own — the shops.
// Both come out of the SAME liked list: a like is a like, and it is keyed by uid
// with no idea what kind of account it points at. Splitting them for display
// rather than in the data is what keeps that true.
//
// Recommendations used to live here too, but they belong on the dashboard where
// people land — see RecommendationsFeed.
export default function MyDesigners() {
  const { user } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();

  const [designers, setDesigners] = useState([]);
  const [shops, setShops] = useState([]);
  const [likedUids, setLikedUids] = useState([]);
  const [loading, setLoading] = useState(true);

  // Extracted so pull-to-refresh can re-run it; `alive` is a ref because a
  // refresh can still be in flight when the page unmounts.
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const load = useCallback(async ({ force = false } = {}) => {
    try {
      const [all, allShops, liked] = await Promise.all([
        fetchDesigners({ force }),
        fetchShops({ force }),
        user?.uid ? fetchLikedUids(user.uid) : Promise.resolve([]),
      ]);
      if (!alive.current) return;
      setDesigners(all);
      setShops(allShops);
      setLikedUids(liked);
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => { load(); }, [load]);
  // Forced: the directory is cached for five minutes, and a like count that
  // did not move is the most likely thing to make a refresh look broken.
  useRefreshable(() => load({ force: true }));

  const byUid = useMemo(
    () => new Map(designers.map(d => [d.uid || d.id, d])),
    [designers],
  );

  const shopByUid = useMemo(
    () => new Map(shops.map(s => [s.uid || s.id, s])),
    [shops],
  );

  // Both derived from the same uid list, in the order it was liked in. A uid is
  // in exactly one directory, so the two lists cannot overlap.
  const likedDesigners = useMemo(
    () => likedUids.map(uid => byUid.get(uid)).filter(Boolean),
    [likedUids, byUid],
  );

  const likedShops = useMemo(
    () => likedUids.map(uid => shopByUid.get(uid)).filter(Boolean),
    [likedUids, shopByUid],
  );

  return (
    <div className="dash-root">
      <header className="dash-nav">
        <NavBrand />
        <div className="dash-nav-right">
          <HeaderSearch />
          <NotificationBell />
          <CreditsBadge />
          <MessagesButton />
          {user ? <UserMenu /> : <Link to="/login" className="sp-signin">{t('nav_login')}</Link>}
        </div>
      </header>

      <BackBar width={1240} />

      <main className="dash-main md-main">
        <h1 className="sp-title">{t('md_title')}</h1>

        {loading ? (
          <PageSpinner />
        ) : (
          <>
            {/* "Nothing yet" only when BOTH are empty — with a liked shop on
                screen, "you haven't liked anyone" above it would be wrong. */}
            {likedDesigners.length === 0 && likedShops.length === 0 ? (
              <p className="sp-empty">{t('md_empty')}</p>
            ) : (
              <>
                {likedDesigners.length > 0 && (
                  <div className="sp-grid">
                    {likedDesigners.map(d => <DesignerCard key={d.uid || d.id} d={d} t={t} />)}
                  </div>
                )}

                {/* Its own section, under a heading of its own — a shop and a
                    designer in one grid would be two kinds of result in a list
                    that looks like one. */}
                {likedShops.length > 0 && (
                  <>
                    <h2 className="md-heading md-heading--liked">{t('md_liked_shops')}</h2>
                    <div className="sp-grid">
                      {likedShops.map(s => <ShopCard key={s.uid || s.id} s={s} t={t} />)}
                    </div>
                  </>
                )}
              </>
            )}

          </>
        )}
      </main>

    </div>
  );
}
